export const launchSteps = [
  'Artwork',
  'Product type',
  'Design',
  'Mockups',
  'Listing',
  'Channels',
  'Review',
  'Publish',
];

export const launchStorageKey = 'podboss.launch-product.v1';

export function emptyLaunchDraft() {
  return {
    step: 0,
    productId: '',
    listingId: '',
    design: null,
    template: null,
    offerId: '',
    productName: '',
    variantIDs: [],
    placementKeys: [],
    placementTransforms: {},
    selectedMockups: [],
    uploadedMockups: [],
    mediaText: '',
    listingTitle: '',
    description: '',
    descriptionTouched: false,
    tagsText: '',
    language: 'en',
    currency: 'USD',
    priceText: '0.00',
    plans: {},
    workflows: {},
  };
}

export function readLaunchDraft(storage = typeof window === 'undefined' ? null : window.localStorage) {
  if (!storage) return emptyLaunchDraft();
  try {
    const parsed = JSON.parse(storage.getItem(launchStorageKey) || 'null');
    if (!parsed || typeof parsed !== 'object') return emptyLaunchDraft();
    const draft = { ...emptyLaunchDraft(), ...parsed };
    draft.placementTransforms = parsed.placementTransforms && typeof parsed.placementTransforms === 'object' && !Array.isArray(parsed.placementTransforms)
      ? Object.fromEntries(Object.entries(parsed.placementTransforms).map(([key, value]) => [key, normalizePlacementTransform(value)]))
      : {};
    draft.selectedMockups = Array.isArray(parsed.selectedMockups) ? parsed.selectedMockups : [];
    draft.uploadedMockups = Array.isArray(parsed.uploadedMockups) ? parsed.uploadedMockups : [];
    draft.descriptionTouched = typeof parsed.descriptionTouched === 'boolean'
      ? parsed.descriptionTouched
      : Boolean(String(parsed.description || '').trim());
    return draft;
  } catch {
    return emptyLaunchDraft();
  }
}

export function writeLaunchDraft(draft, storage = typeof window === 'undefined' ? null : window.localStorage) {
  if (!storage) return;
  try {
    storage.setItem(launchStorageKey, JSON.stringify(draft));
  } catch {
    // A server-backed draft remains safe even if browser storage is unavailable.
  }
}

export function tagsFromText(value) {
  const seen = new Set();
  return String(value || '')
    .split(/[\n,]+/)
    .map((item) => item.trim().replace(/\s+/g, ' '))
    .filter((item) => {
      const key = item.toLowerCase();
      if (!item || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function mediaURLsFromText(value) {
  return Array.from(new Set(String(value || '').split(/\r?\n/).map((item) => item.trim()).filter(Boolean)));
}

function launchMediaIdentity(item) {
  const imageID = String(item?.image_id || '').trim();
  if (imageID) return `image:${imageID}`;
  const sourceURL = String(item?.source_url || item?.url || '').trim();
  return sourceURL ? `url:${sourceURL}` : '';
}

function launchMediaMetadata(item) {
  const metadata = {};
  if (item?.source === 'provider' && item?.id) metadata.catalog_mockup_id = item.id;
  if (item?.image_id) metadata.asset_id = item.image_id;
  if (item?.source) metadata.source = item.source;
  if (item?.provider) metadata.provider = item.provider;
  if (item?.offer_id) metadata.fulfillment_offer_id = item.offer_id;
  if (item?.filename) metadata.filename = item.filename;
  return metadata;
}

export function launchMediaFromDraft(draft) {
  const result = [];
  const identities = new Set();
  const sourceURLs = new Set();
  const append = (item) => {
    const normalized = {
      id: String(item?.id || '').trim(),
      image_id: String(item?.image_id || '').trim() || null,
      source_url: String(item?.source_url || item?.url || '').trim(),
      preview_url: String(item?.preview_url || item?.thumbnail_url || item?.source_url || item?.url || '').trim(),
      media_type: String(item?.media_type || 'image').trim().toLowerCase() || 'image',
      alt_text: String(item?.alt_text || '').trim(),
      source: String(item?.source || '').trim(),
      provider: String(item?.provider || '').trim(),
      offer_id: String(item?.offer_id || '').trim(),
      filename: String(item?.filename || '').trim(),
    };
    const identity = launchMediaIdentity(normalized);
    if (!identity || identities.has(identity) || (normalized.source_url && sourceURLs.has(normalized.source_url))) return;
    identities.add(identity);
    if (normalized.source_url) sourceURLs.add(normalized.source_url);
    result.push(normalized);
  };

  (Array.isArray(draft?.selectedMockups) ? draft.selectedMockups : []).forEach(append);
  mediaURLsFromText(draft?.mediaText).forEach((sourceURL) => append({
    id: `url:${sourceURL}`,
    source_url: sourceURL,
    preview_url: sourceURL,
    media_type: 'image',
    source: 'url',
  }));
  return result;
}

export function centsFromText(value) {
  const amount = Number.parseFloat(String(value || '').trim());
  if (!Number.isFinite(amount) || amount < 0) throw new Error('Price must be a valid non-negative amount.');
  return Math.round(amount * 100);
}

function objectValue(value) {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

const finiteNumber = (value, fallback) => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const boundedNumber = (value, fallback, minimum, maximum) => Math.min(maximum, Math.max(minimum, finiteNumber(value, fallback)));

// Product placements use a provider-neutral, normalized coordinate system.
// x/y are the layer center within the printable surface, scale is artwork
// width relative to printable-surface width, and angle is clockwise degrees.
// Adapters translate this intent into provider-native print-area fields.
export function normalizePlacementTransform(value) {
  const source = objectValue(value);
  const pattern = objectValue(source.pattern);
  const normalized = {
    schema_version: 1,
    x: boundedNumber(source.x, 0.5, -1, 2),
    y: boundedNumber(source.y, 0.5, -1, 2),
    scale: boundedNumber(source.scale, 1, 0.05, 10),
    angle: boundedNumber(source.angle ?? source.rotation, 0, -360, 360),
  };
  if (Object.keys(pattern).length) normalized.pattern = pattern;
  return normalized;
}

export function placementTransformForDraft(draft, placement) {
  const key = String(placement?.placement_key || '').trim();
  const saved = key ? draft?.placementTransforms?.[key] : null;
  return normalizePlacementTransform(saved || placement?.default_transform);
}

export function defaultProductName(design, template) {
  const designName = String(design?.filename || '').replace(/\.[^.]+$/, '').trim();
  const productType = String(template?.product_type || template?.name || '').trim();
  return [designName, productType].filter(Boolean).join(' - ');
}

export function buildLaunchProductPayload({ draft, catalogDetail, designDetail, currentProduct }) {
  const offer = (catalogDetail?.offers || []).find((item) => item.id === draft.offerId);
  if (!draft.design?.id) throw new Error('Choose a design.');
  if (!draft.template?.id) throw new Error('Choose a product type.');
  if (!offer) throw new Error('Choose an available fulfillment offer.');
  if (!String(draft.productName || '').trim()) throw new Error('Enter a product name.');

  const selectedVariants = new Set(draft.variantIDs || []);
  const currentVariants = new Map((currentProduct?.variants || [])
    .filter((item) => item.fulfillment_offer_variant_id)
    .map((item) => [item.fulfillment_offer_variant_id, item]));
  const variants = (offer.variants || [])
    .filter((item) => selectedVariants.has(item.id))
    .map((item, index) => ({
      ...(currentVariants.get(item.id)?.id ? { id: currentVariants.get(item.id).id } : {}),
      fulfillment_offer_variant_id: item.id,
      sku: currentVariants.get(item.id)?.sku || '',
      title: item.title || item.external_id,
      options: objectValue(item.options),
      enabled: item.available !== false,
      sort_order: index,
    }));
  (currentProduct?.variants || []).forEach((item) => {
    if (item.id && (!item.fulfillment_offer_variant_id || !selectedVariants.has(item.fulfillment_offer_variant_id))) variants.push({ id: item.id, remove: true });
  });

  const selectedPlacements = new Set(draft.placementKeys || []);
  const currentPlacements = new Map();
  (currentProduct?.placements || [])
    .filter((item) => !item.product_variant_id)
    .forEach((item) => {
      // Product placements are returned in layer order. This editor owns the
      // base layer and leaves later layers untouched until the layer panel is
      // introduced.
      if (!currentPlacements.has(item.placement_key)) currentPlacements.set(item.placement_key, item);
    });
  const placements = (offer.placements || [])
    .filter((item) => selectedPlacements.has(item.placement_key))
    .map((item, index) => ({
      ...(currentPlacements.get(item.placement_key)?.id ? { id: currentPlacements.get(item.placement_key).id } : {}),
      image_id: designDetail?.image_id || null,
      product_variant_id: null,
      placement_key: item.placement_key,
      transform: placementTransformForDraft(draft, item),
      sort_order: index,
    }));
  (currentProduct?.placements || []).forEach((item) => {
    if (item.id && !selectedPlacements.has(item.placement_key)) placements.push({ id: item.id, remove: true });
  });

  return {
    ...(currentProduct?.version ? { expected_version: currentProduct.version } : {}),
    name: draft.productName.trim(),
    status: currentProduct?.status || 'draft',
    design_id: draft.design.id,
    catalog_template_id: draft.template.id,
    default_fulfillment_offer_id: offer.id,
    variants,
    placements,
  };
}

export function buildLaunchListingPayload({ draft, product, currentListing, desiredStatus }) {
  if (!product?.id) throw new Error('Save the Product before creating its Listing.');
  const price = centsFromText(draft.priceText);
  const currentVariants = new Map((currentListing?.variants || []).filter((item) => item.product_variant_id).map((item) => [item.product_variant_id, item]));
  const variants = (product.variants || []).filter((item) => item.enabled !== false).map((item) => ({
    ...(currentVariants.get(item.id)?.id ? { id: currentVariants.get(item.id).id } : {}),
    product_variant_id: item.id,
    sku: currentVariants.get(item.id)?.sku || item.sku || '',
    price,
    currency: String(draft.currency || 'USD').toUpperCase(),
    enabled: true,
  }));
  const selectedProductVariants = new Set((product.variants || []).map((item) => item.id));
  (currentListing?.variants || []).forEach((item) => {
    if (item.id && (!item.product_variant_id || !selectedProductVariants.has(item.product_variant_id))) variants.push({ id: item.id, remove: true });
  });

  const selectedMedia = launchMediaFromDraft(draft);
  const currentMedia = new Map();
  (currentListing?.media || []).forEach((item) => {
    const identity = launchMediaIdentity(item);
    if (identity) currentMedia.set(identity, item);
  });
  const media = selectedMedia.map((item, index) => {
    const identity = launchMediaIdentity(item);
    const current = currentMedia.get(identity);
    return {
      ...(current?.id ? { id: current.id } : {}),
      source_url: item.source_url,
      image_id: item.image_id,
      media_type: item.media_type,
      position: index,
      featured: index === 0,
      alt_text: item.alt_text || `${draft.listingTitle || draft.productName} mockup ${index + 1}`.trim(),
      metadata: launchMediaMetadata(item),
    };
  });
  const selectedMediaIDs = new Set(selectedMedia.map(launchMediaIdentity));
  (currentListing?.media || []).forEach((item) => {
    if (item.id && !selectedMediaIDs.has(launchMediaIdentity(item))) media.push({ id: item.id, remove: true });
  });

  return {
    ...(currentListing?.version ? { expected_version: currentListing.version } : { product_id: product.id }),
    title: String(draft.listingTitle || draft.productName || '').trim(),
    description: String(draft.description || '').trim(),
    tags: tagsFromText(draft.tagsText),
    language: String(draft.language || 'en').toLowerCase(),
    currency: String(draft.currency || 'USD').toUpperCase(),
    status: desiredStatus || currentListing?.status || 'draft',
    variants,
    media,
  };
}

export function launchErrorMessage(error, fallback) {
  const validationMessages = (Array.isArray(error?.validation?.errors) ? error.validation.errors : [])
    .map((issue) => String(issue?.message || '').trim())
    .filter(Boolean);
  if (validationMessages.length) return Array.from(new Set(validationMessages)).join(' ');
  return error?.message || fallback;
}

export function maximumLaunchStep(draft) {
  if (!draft.design?.id) return 0;
  if (!draft.template?.id || !draft.offerId) return 1;
  if (!draft.productId) return 2;
  if (!draft.listingId) return 4;
  if (!Object.keys(draft.plans || {}).length) return 6;
  return 7;
}
